// Import Packages
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

// // Import Pages
import { saveAvailable, changedFields, initialActions, renameTime, activeAutom, errorHandlingActiveAutom, alertState } from '../../../../../../../../recoil-store/recoil-state';

// Import Helper
import { handleUpdate } from '../../../../../../../../helpers/flowHistory';

const ItemLabel = React.memo(({
    item,
    updateVariableByItemId,
    setChangeLabel,
    changeLabelField,
    setActions,
    isEhFlow
}) => {
    const { t } = useTranslation();
    const [actionLabel, setActionLabel] = useState((item.action_name === 'GROUP' || item.action_name === 'BUTTON-SCOPE' || item.action_name === 'LIST-SCOPE' || item.action_name === 'TEXT')
        && item.fields?.label?.value ?
        item.fields.label.value : item?.label);
    const setChangeFields = useSetRecoilState(changedFields);
    const setRenameTime = useSetRecoilState(renameTime);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const setAutomDetail = useSetRecoilState(isEhFlow ? errorHandlingActiveAutom : activeAutom);
    const setAlert = useSetRecoilState(alertState);
    const initialActionsData = useRecoilValue(initialActions);

    // const copiedItem = useRecoilValue(copiedItemState);

    const onChangeLabel = (e) => {
        if (e.target.value?.length > 40) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.AUTOMS.MAXIMUM-LENGTH-40'),
                severity: 'error'
            }));
        }
        setActionLabel(e.target.value)
    }

    const checkAndRenameGoto = (items, gotoIds, newGoToName) => {
        return new Promise(async (resolve) => {
            for (let action of items) {
                if (action?.actionName === 'GO-TO') {
                    if (action.fields && action.fields.destination?.value?.item_id && action.fields.destination.value.item_id === item.item_id) {
                        action.fields.destination.value.name = newGoToName;
                        gotoIds.push(action.id)
                    }
                }

                if (action?.children && action.children.length) {
                    await checkAndRenameGoto(action.children, gotoIds, newGoToName)
                }

            }
            resolve(true);
        })
    }

    const saveLabel = async (isCustomAction) => {
        setChangeLabel(false);
        let copyItems
        let data = { found: false };
        let nameCount = { v: 0 };
        let gotoIds;
        let newGoToName;
        let errorHandlingId;
        setAutomDetail((autom) => {
            errorHandlingId = autom.errorHandlingId;
            copyItems = JSON.parse(JSON.stringify(autom.items));
            return autom
        })
        findAndSetLabel(copyItems, data, nameCount)
        // Checking if actions field is not changing
        if (data.found) {
            gotoIds = [];
            newGoToName = actionLabel;
            if (!actionLabel) {
                newGoToName = item.library_label.includes('CUSTOM') ? item?.library_name + " - " + item.name :
                    (item.libraryName === 'STOP' ? '' : t(`CONTENT.LIBRARY.${item.libraryName}.NAME`) + " - ") +
                    t(`CONTENT.LIBRARY.${item.libraryName}.ACTION.${item.name}`);
                if (nameCount.v > 1) {
                    newGoToName += ` (${nameCount.v})`;
                }
            }

            await checkAndRenameGoto(copyItems, gotoIds, newGoToName)
            if (gotoIds.length) {
                setActions(prev => {
                    return gotoIds.reduce((newState, key) => {
                        // If the key does not exist, do not change it
                        if (!prev[key]) return newState;

                        // Create new state for key
                        return {
                            ...newState,
                            [key]: {
                                ...prev[key],
                                fields: {
                                    ...prev[key].fields,
                                    destination: {
                                        ...prev[key].fields.destination,
                                        value: {
                                            ...prev[key].fields.destination.value,
                                            name: newGoToName // update name
                                        }
                                    }
                                }

                            }
                        };
                    }, { ...prev }); // Use old state at startup
                })
            }
            //if (isCustomAction) {
            updateVariableByItemId(item.item_id, actionLabel);
            // }
            setAutomDetail((autom) => {
                return { ...autom, items: copyItems }
            });
            isEhFlow ? handleUpdate(copyItems, undefined, undefined, "errorHistory", errorHandlingId) : handleUpdate(copyItems)
            setRenameTime(new Date());
        }

        const labelChangeManagement = () => {
            let oldValue = initialActionsData[item.id]?.label;
            if (!oldValue) {
                if (item?.library_label?.includes('CUSTOM')) {
                    oldValue = item?.action_name
                } else {
                    oldValue = t('CONTENT.LIBRARY.' + item.libraryName + '.ACTION.' + item.name)
                }
            }

            let input_name = 'xAction_label';

            // The operations here were performed without any performance problems. There is a performance issue when using the ChangeFields value.
            if (oldValue !== actionLabel) {
                if (["TEXT", "GROUP", "BUTTON-ITEM", "BUTTON-SCOPE", "DYNAMIC-LIST", "LIST-ITEM", "LIST-SCOPE", "SEND-MEDIA"].includes(item.action_name)) {
                    changeLabelField(item.id, actionLabel)
                }
                setChangeFields((prev) => {
                    let cfTemp = JSON.parse(JSON.stringify(prev));
                    if (!cfTemp[item.item_id]) {
                        cfTemp[item.item_id] = [];
                    }

                    if (!cfTemp[item.item_id].includes(input_name)) {
                        cfTemp[item.item_id].push(input_name);
                    }
                    return cfTemp;
                });
                setSaveAvailable(true);
            } else {
                let saveAvailable;
                setChangeFields((prev) => {
                    let cfTemp = JSON.parse(JSON.stringify(prev));
                    if (cfTemp[item.item_id]) {
                        let indexToDelete = cfTemp[item.item_id].indexOf(input_name)

                        // if the value is found in the array, remove it
                        if (indexToDelete > -1) {
                            cfTemp[item.item_id].splice(indexToDelete, 1);
                        }

                        if (cfTemp[item.item_id].length < 1) {
                            delete cfTemp[item.item_id];
                        }
                    }
                    if (Object.keys(cfTemp).length < 1) {
                        saveAvailable = false;
                    } else {
                        saveAvailable = true;
                    }
                    return cfTemp;
                });
                setTimeout(() => {
                    // Written to be safe due to asynchronous operation
                    setSaveAvailable(saveAvailable);
                }, 300)
            }
        };

        labelChangeManagement();
    }

    const findAndSetLabel = (items, data, nameCount) => {
        for (const i in items) {
            if (item.library_name === items[i].library_name && item.name === items[i].name) {
                nameCount.v++;
            }
            if (items[i].item_id === item.item_id) {
                if (items[i].label !== actionLabel) {
                    items[i].label = actionLabel
                    if (["TEXT", "GROUP", "BUTTON-ITEM", "BUTTON-SCOPE", "DYNAMIC-LIST", "LIST-ITEM", "LIST-SCOPE", "SEND-MEDIA"].includes(item.action_name)) {
                        items[i].fields.label = { value: actionLabel };
                    }
                    data.found = true;
                }
                break;
            } else if (items[i].hasOwnProperty('children')) {
                items[i].children = findAndSetLabel(items[i].children, data, nameCount);
                if (data.found) {
                    break;
                }
            }
        }
        return items;
    }


    return (
        <>
            <input style={{ border: '0' }} className="action-item-action action-item-label-input"
                autoFocus type="text" onBlur={() => saveLabel(false)}
                value={actionLabel} onChange={onChangeLabel} />
        </>
    );
});
export default ItemLabel;
